import isEmpty from 'lodash/isEmpty';
import { numberFormat } from '../helpers/utils';
import { dateFriendly } from '../helpers/date';
import { getProductStatusValue } from '../helpers/sku';
import { priceFormat, priceFromAPI } from '../helpers/price';

/**
 * transform channel
 * @param {Object} channel
 * @returns {Object} channel
 */
export const transformQtyChannel = (data = {}) => {
	return {
		id: data.id || '',
		name: data.name || '',
		quantity: data.quantity !== undefined ? Number(data.quantity) : '-',
	};
};

/**
 * transform channels
 * @param {Array} channels
 * @returns {Array} channels
 */
export const transformQtyChannels = (data = []) => {
	return data.map((item) => transformQtyChannel(item));
};

/**
 * transform stock commitments
 * @param {Object}
 * @returns {Object}
 */
export const transformStockCommitments = (data = {}) => {
	return {
		committed: data.committed,
		reserved: data.reserved,
	};
};

/**
 * transform stock sku
 * @param {Object} sku
 * @returns {Object} sku info
 */
export const transformStockSku = (input = {}) => {
	const availableStock = input?.stocks?.available;
	const onlineStock = input?.stocks?.online;
	const offlineStock = input?.stocks?.offline;
	const totalStock = input?.stocks?.total;
	const skuStatus = (() => {
		if (input.is_eol !== undefined && input.is_active !== undefined) {
			return getProductStatusValue(input.is_eol, input.is_active);
		}
		return null;
	})();

	return {
		id: input.id || null,
		image: input.images.length > 0 ? input.images[0].urls.thumbnail : null,
		sku: input.sku || null,
		product_name: input.name || null,
		brand: input.brand || null,
		available_stock: availableStock !== undefined ? numberFormat(availableStock) : null,
		online_stock: onlineStock !== undefined ? numberFormat(onlineStock) : null,
		offline_stock: offlineStock !== undefined ? numberFormat(offlineStock) : null,
		total_stock: totalStock !== undefined ? numberFormat(totalStock) : null,
		group: input.group || {},
		channelOrderQty: input.channel_order_qty || {},
		channels: transformQtyChannels(input.channels),
		reserved: 0,
		commited: 0,
		status: skuStatus,
		type: input.type || null,
		eolStatus: input.is_eol || null,
		last_synced: input.product_synced_at ? dateFriendly(input.product_synced_at, 'DD MMM YYYY HH:mm') : null,
		stockCommitments: transformStockCommitments(input.stock_commitments),
	};
};

/**
 * transform skus
 * @param {Array} sku
 * @returns {Array} sku list
 */
export const transformStockSkus = (data = []) => {
	return data.map((item) => transformStockSku(item));
};

/**
 * transform online stock rule
 * @param {Object} rule
 * @returns {Object} rule
 */
export const transformOnlineStockRule = (data = {}) => {
	return {
		id: data.id,
		minimum: data.minimum,
		name: data.name,
		onlineRatio: data.online_ratio,
		priority: data.priority,
		status: Boolean(data.is_active),
	};
};

/**
 * transform online stock rule list
 * @param {Object} online_stock
 * @returns {Object} skus
 */
export const transformOnlineStockRuleList = (data = []) => (
	data.map(transformOnlineStockRule)
);

/**
 * transform channel rule
 * @param {Object} rule
 * @returns {Object} rule
 */
export const transformChannelRule = (data = {}) => {
	return {
		id: data.id,
		name: data.name,
		priority: data.priority,
		status: Boolean(data.is_active),
	};
};

/**
 * transform channel rule list
 * @param {Object} skus
 * @returns {Object} skus
 */
export const transformChannelRuleList = (data = []) => (
	data.map(transformChannelRule)
);

/**
 * transform stock sku detail
 * @param {Object} detail
 * @returns {Object} detail
 */
export const transformStockSkuDetail = (data = {}) => {
	const srpPrice = data.srp_price;
	const minPrice = data.min_price;
	const totalStock = data.total_stock;
	const onlineStock = data.online_stock;
	const offlineStock = data.offline_stock;
	const skuLastSynced = data.sku_last_synced;
	const stockLastSynced = data.stock_last_synced;
	const skuStatus = (() => {
		if (data.is_eol !== undefined && data.is_active !== undefined) {
			return getProductStatusValue(data.is_eol, data.is_active);
		}
		return null;
	})();

	return {
		imageThumbnail: data.image_thumbnail || null,
		imageOriginal: data.image_original || null,
		sku: data.sku || null,
		isActive: data.is_active,
		isAddStockAllowed: data.is_add_stock_allowed,
		isEol: data.is_eol,
		status: skuStatus,
		productName: data.product_name || null,
		categories: data.categories || [],
		brand: data.brand || null,
		productType: data.product_type || null,
		srpPrice: srpPrice !== undefined ? priceFormat(priceFromAPI(srpPrice)) : '-',
		minPrice: minPrice !== undefined ? priceFormat(priceFromAPI(minPrice)) : '-',
		totalStock: totalStock !== undefined ? numberFormat(totalStock) : '-',
		onlineStock: onlineStock !== undefined ? numberFormat(onlineStock) : '-',
		offlineStock: offlineStock !== undefined ? numberFormat(offlineStock) : '-',
		skuLastSynced: skuLastSynced ? dateFriendly(skuLastSynced, 'DD MMM YYYY HH:mm') : '-',
		stockLastSynced: stockLastSynced ? dateFriendly(stockLastSynced, 'DD MMM YYYY HH:mm') : '-',
		onlineStockRule: transformOnlineStockRuleList(data.online_stock_rule),
		channelRule: transformChannelRuleList(data.channel_rule),
	};
};

/**
 * transform stock sku branch
 * @param {Object} sku
 * @returns {Object} sku
 */
export const transformStockSkuBranch = (data = {}) => {
	const onlineStock = data.online_stock_quantity;
	const offlineStock = data.offline_stock_quantity;
	const totalStock = data.total_stock_quantity;

	return {
		itecBranchId: data.itec_branch_id,
		branchName: data.branch_name_th,
		onlineStock: onlineStock !== undefined ? numberFormat(onlineStock) : null,
		offlineStock: offlineStock !== undefined ? numberFormat(offlineStock) : null,
		totalStock: totalStock !== undefined ? numberFormat(totalStock) : null,
		location: data.location,
	};
};

/**
 * transform stock sku branch
 * @param {Object} skus
 * @returns {Object} skus
 */
export const transformStockSkuBranches = (data = []) => {
	return data.map((item) => transformStockSkuBranch(item));
};

/**
 * transform stock sku branch
 * @param {Object} sku
 * @returns {Object} sku branch info
 */
export const transformStockBranch = (input = {}) => {
	return {
		branch_id_of_im: input.id || null,
		id: input.itec_branch_id || null,
		branch_id: input.itec_branch_id || null,
		branch_name: input.branch_name_th || null,
		total_brand: input.total_brand !== undefined ? numberFormat(input.total_brand) : null,
		total_sku: input.total_sku !== undefined ? numberFormat(input.total_sku) : null,
		total_stock: input.total_stock !== undefined ? numberFormat(input.total_stock) : null,
		online_percentage: input.online_percentage !== undefined ? `${input.online_percentage}%` : null,
		offline_percentage: input.offline_percentage !== undefined ? `${input.offline_percentage}%` : null,
		location: input.location || null,
		last_synced: input.last_synced ? dateFriendly(input.last_synced, 'DD MMM YYYY HH:mm') : null,
	};
};

/**
 * transform stock branches
 * @param {Array} branches
 * @returns {Array} branches
 */
export const transformStockBranches = (data = []) => {
	return data.map((item) => transformStockBranch(item));
};

/**
 * transform offline stocks
 * @param {Object} stocks
 * @returns {Object} stocks
 */
export const transformOfflineStock = (data = {}) => {
	if (isEmpty(data)) {
		return null;
	}
	return {
		items: data.offline.items !== undefined ? data.offline.items : [],
		totalQuantity: data.offline.total_quantity !== undefined ? Number(data.offline.total_quantity) : null,
		percentage: data.offline.percentage !== undefined ? data.offline.percentage : null,
	};
};

/**
 * transform online stocks
 * @param {Object} stocks
 * @returns {Object} stocks
 */
export const transformOnlineStock = (data = {}) => {
	if (isEmpty(data)) {
		return null;
	}
	return {
		items: data.online.items !== undefined ? data.online.items : [],
		totalQuantity: data.online.total_quantity !== undefined ? Number(data.online.total_quantity) : null,
		percentage: data.online.percentage !== undefined ? data.online.percentage : null,
	};
};

/**
 * transform stock branches detail
 * @param {Object} detail
 * @returns {Object} detail
 */
export const transformStockBranchesDetail = (data = {}) => {
	return {
		id: data.id,
		itecBranchId: data.itec_branch_id,
		branchName: data.branch_name_th,
		location: data.location,
		totalStock: data.total_stock !== undefined ? numberFormat(data.total_stock) : null,
		totalBrand: data.total_brand !== undefined ? numberFormat(data.total_brand) : null,
		totalSku: data.total_sku !== undefined ? numberFormat(data.total_sku) : null,
		totalAllocatedStock: data.total_allocated_stock,
		onlineStock: transformOnlineStock(data.channels),
		offlineStock: transformOfflineStock(data.channels),
	};
};

/**
 * transform stock branches sku
 * @param {Object} sku
 * @returns {Object} sku
 */
export const transformStockBranchesSku = (data = {}) => {
	const skuStatus = (() => {
		if (data.is_eol !== undefined && data.is_active !== undefined) {
			return getProductStatusValue(data.is_eol, data.is_active);
		}
		return null;
	})();

	return {
		imageThumbnail: data.image_thumbnail,
		sku: data.sku,
		productName: data.product_name,
		brand: data.brand,
		availableStock: data.available_stock !== undefined ? numberFormat(data.available_stock) : null,
		categories: data.categories || [],
		type: data.type,
		status: skuStatus,
		lastSynced: data.last_synced ? dateFriendly(data.last_synced, 'DD MMM YYYY HH:mm') : null,
	};
};

/**
 * transform branches skus
 * @param {Object} skus
 * @returns {Object} skus
 */
export const transformStockBranchesSkus = (data = []) => {
	return data.map((item) => transformStockBranchesSku(item));
};
